import React, { useEffect, useState } from 'react';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import Divider from '@mui/material/Divider';
import { LoanNumber } from 'Feature/Arrears/components/ArrearsInquiryPage/core/LoanNumber';
import SubHeader from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/SubHeader';
import { useTranslation } from 'react-i18next';
import { TextFormFieldError } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/form/TextFormFieldError';

interface SearchByLoanNumberInputProps {
  loanNumber: LoanNumber;
  setLoanNumber: (loanNumber: LoanNumber) => void;
}

const SearchByLoanNumberInput = ({ loanNumber, setLoanNumber }: SearchByLoanNumberInputProps) => {
  const { t } = useTranslation('HbtArrearsInquiryPage');
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    if (loanNumber.status === 'empty') {
      setValue('');
    }
  }, [loanNumber]);

  const clearLoanNumber = () => {
    setLoanNumber(LoanNumber.empty());
  };

  const searchLoanNumber = (value: string) => {
    const pattern = /^[1-9]\d{7}$/;
    if (pattern.test(value)) {
      setLoanNumber(LoanNumber.valid(parseInt(value, 10)));
    } else {
      setLoanNumber(LoanNumber.invalid(value));
    }
  };

  const onLoanNumberChange = (value: string) => {
    const pattern = /^(?:[1-9]\d{0,7})?\s*$/;
    if (pattern.test(value)) {
      setValue(value.trim());
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchLoanNumber(value);
    }
  };

  const error =
    loanNumber.status === 'invalid'
      ? t('searchByLoanNumberInput.error', { loanNumber: loanNumber.value })
      : undefined;

  return (
    <div>
      <SubHeader>{t('searchByLoanNumberInput.description')}</SubHeader>
      <TextField
        placeholder={t('searchByLoanNumberInput.placeholder')}
        sx={{
          maxWidth: '644px',
          width: '100%',
          '& .MuiOutlinedInput-notchedOutline': {
            border: '2px solid #B4BCC2'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: '2px solid #697885'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '2px solid #697885'
          },
          '&:focus-within .MuiOutlinedInput-notchedOutline': {
            border: '2px solid #697885'
          },
          '& .MuiInputBase-input::placeholder': {
            fontSize: '14px',
            color: '#1F2428',
            fontWeight: '400',
            lineHeight: '21px'
          },
          '& .MuiInputBase-input': {
            fontSize: '14px',
            color: '#1F2428',
            fontWeight: '400',
            lineHeight: '21px'
          },
          '& .MuiFormHelperText-root': {
            marginLeft: 0
          },
          '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#DA291C'
          },
          '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#DA291C'
          },
          '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#DA291C'
          },
          '& .MuiOutlinedInput-root': {
            '&.Mui-disabled': {
              backgroundColor: '#f0f2f3',
              '& fieldset': {
                border: 'none'
              },
              '& input': {
                color: '#1f2428',
                WebkitTextFillColor: 'unset'
              }
            }
          }
        }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Box display="flex" flexDirection="row" gap="16px">
                  {value && (
                    <>
                      <IconButton aria-label="clear search button" onClick={clearLoanNumber}>
                        <CmhcIcon type="icon_clear_circle" size={CmhcIconSize.SMALL} />
                      </IconButton>
                      <Divider orientation="vertical" variant="fullWidth" flexItem />
                    </>
                  )}
                  <IconButton aria-label="search button" onClick={() => searchLoanNumber(value)}>
                    <CmhcIcon type="icon_search" size={CmhcIconSize.SMALL} />
                  </IconButton>
                </Box>
              </InputAdornment>
            )
          }
        }}
        onChange={(e) => onLoanNumberChange(e.target.value)}
        onKeyDown={onKeyDown}
        value={value}
        error={error !== undefined}
        helperText={<TextFormFieldError error={error} />}
      />
    </div>
  );
};

export default SearchByLoanNumberInput;
